export interface GovernanceDebtItem {
  description: string;
  category: 'Missing Policy' | 'Missing Consultation' | 'Missing Review' | 'Missing Control';
  severity: 'Low' | 'Medium' | 'High' | 'Critical';
}

export interface SavedAssessment {
  id: string;
  systemName: string;
  organization: string;
  sector: string;
  deploymentStage: string;
  impactLevel: string;
  autonomyLevel: string;
  dataAccessed: string;
  aiActions: string;
  affectedGroups: string;
  organizationValues: string;
  existingPolicy: string;
  stakeholderConsultation: string;
  riskScore: number;
  riskCategory: string;
  decisionRecommendation: string;
  governanceMaturity: string;
  confidenceLevel: string;
  status: 'Approved' | 'Under Review' | 'Rejected' | 'Deferred';
  createdAt: string;
  governanceDebt?: GovernanceDebtItem[];
  debtSeverity?: string;
  fullReport: Record<string, unknown>;
}

/**
 * Save an assessment via the server-side API route (persists to disk).
 */
export async function saveAssessment(
  assessment: Omit<SavedAssessment, 'id' | 'createdAt'>
): Promise<SavedAssessment> {
  const res = await fetch('/api/assessments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(assessment),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save assessment');
  }
  const data = await res.json();
  return data.assessment as SavedAssessment;
}

/**
 * Update an existing assessment by ID.
 */
export async function updateAssessment(
  id: string,
  assessment: Omit<SavedAssessment, 'id' | 'createdAt'>
): Promise<SavedAssessment> {
  const res = await fetch('/api/assessments', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, ...assessment }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update assessment');
  }
  const data = await res.json();
  return data.assessment as SavedAssessment;
}

/**
 * Delete an assessment by ID.
 */
export async function deleteAssessment(id: string): Promise<void> {
  const res = await fetch(`/api/assessments?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to delete assessment');
  }
}

/**
 * Load all assessments from the server-side API route.
 */
export async function loadAssessments(): Promise<SavedAssessment[]> {
  const res = await fetch('/api/assessments', { cache: 'no-store' });
  if (!res.ok) return [];
  const data = await res.json();
  return (data.assessments || []) as SavedAssessment[];
}
